import WidgetView from 'scoreboard/dashboards/widgets/widget-view'
import loadingDots from 'scoreboard/components/loading-dots.hbs'
import WidgetEmptyState from '../widget-empty-state'
import _ from 'lodash'
import getChartAccessoryMetadata from './get-chart-accessory-metadata'
import getConfigMenu from './get-config-menu'

/**
 * A parent view for chart accessories, tables or legends that should
 * automatically appear alongside a chart's widget in accordance with the
 * chart's settings
 */
const ChartAccessoryWidgetView = WidgetView.extend({

	/**
	 * handlebars template for the accessory
	 */
	accessoryTemplate: null,

	nonFetchProperties: WidgetView.prototype.nonFetchProperties
		.concat('chartId', 'chartTempId', 'label', 'autoFit'),

	initialize: function() {
		WidgetView.prototype.initialize.apply(this, arguments)

		// when the wob is deleted, turn off the appropriate chart setting
		this.listenTo(this.model.wob, 'destroy', () => {
			this.removeFromChartWidget()
		})

		this.accessoryMetadata =
			getChartAccessoryMetadata(this.model.get('type'))

		let chartWob = this._findChartWob()
		if (chartWob && chartWob.widget) {
			// if the chart widget is destroyed, the corresponding accessory
			// widgets should be destroyed too
			this.listenTo(chartWob, 'destroy', () => {
				this.model.destroy()
			})

			this.listenTo(chartWob.widget.chart.data, 'fetch:success', () => {
				this.renderContent()
			})

			const chartProperty = this.accessoryMetadata.chartProperty
			this.listenTo(chartWob.widget.chart, `change:${chartProperty}`,
				(chartDesigner) => {
				if (this.accessoryMetadata.isEnabled(chartDesigner)) {
					// if the accessory setting was just changed, rerender the
					// content
					this.renderContent()
				}
			})
		}

		this.listenTo(this.model.properties, 'change',
			() => this.renderContent())

		this.firstRender = true
	},

	render: function() {
		WidgetView.prototype.render.apply(this, arguments)
		if (this.$('.content').length === 0) {
			this.$el.append(this.accessoryTemplate())
		}

		this.readyForDisplayAndExport()
		return this
	},

	_findChartWob: function () {
		var chartId = this.model.properties.get('chartId')
		var chartTempId = this.model.properties.get('chartTempId')

		return this.canvas.collection.chain().filter(function (wob) {
				return wob.widget.get('type') === 'chart'
			})
			.find(function (wob) {
				var chart = wob.widget.chart
				if (chart.id && chartId) {
					return chart.id == chartId
				} else if (chartTempId && chart.get('tempId') === chartTempId) {
					return true
				}
			}).value()
	},

	renderError: function () {
		let chartWob = this._findChartWob()
		let chart = chartWob.widget.chart

		this.removeEmptyState()
		this.showContent(false)

		this.emptyState = new WidgetEmptyState({
			errorResponse: chart.get('error'),
			container: this.container,
			widgetType: this.accessoryMetadata.widgetType
		})

		this.$('.empty-state-container').append(this.emptyState.el)
		this.$('.content').addClass('has-widget-error')
		this.emptyState.render()

		this.toggleLoading(false)
		this.readyForDisplayAndExport()
	},

	showContent(show) {
		this.$('.panel').toggleClass('is-not-displayed', !show)
		this.$('.empty-state-container').toggleClass('is-not-displayed', show)
	},

	_createConfigView: function () {
		let Constructor = getConfigMenu(this.model.get('type'))
		return new Constructor({
			model: this.model,
			canvas: this.canvas,
			chartWidget: this._findChartWob().widget,
			widgetUpdater: this.widgetUpdater
		})
	},

	toggleLoading: function (loading) {
		this.$el.toggleClass('is-loading', loading)
		if (loading) {
			this.$el.append(loadingDots())
		} else {
			this.$('.loading-dots').remove()
		}
	},

	/**
	 * Adjusts the size of the wob to fit its contents
	 *
	 * @param fit.height true if the height should be adjusted, false otherwise
	 * @param fit.width true if the width should be adjusted, false otherwise
	 */
	autoFit: function (fit) {
		const dims = fit || this.defaultFit()
		const { height, width } = dims
		// Wait until canvas has made the DOM changes, because we need to measure
		// the elements in the DOM
		this.canvas.onPaint(() => {
			let newHeight = null
			let newWidth = null

			let content = this.$('.content')

			function borderWidth (side) {
				return parseInt(content.css(`border-${side}-width`))
			}

			const widgetContent = this.getWidgetContent()
			if (height) {
				let borderTopWidth = borderWidth('top')
				let borderBottomWidth =  borderWidth('bottom')

				// We can't rely on the height of the `.widget` because it fills the wob.
				// We want to set the height of the wob so it matches the content, so
				// calculate the content height (the HTML table) and then include the
				// `.widget`'s top and bottom border width so the wob doesn't have
				// scrollbars. See #20709 for more details.
				newHeight = widgetContent.offsetHeight + borderBottomWidth + borderTopWidth
			}

			if (width) {
				let borderLeftWidth =  borderWidth('left')
				let borderRightWidth =  borderWidth('right')

				// See `newHeight` calculation for explanation
				newWidth = widgetContent.offsetWidth + borderLeftWidth + borderRightWidth
			}

			this.model.wob.resize(newWidth, newHeight)
		})
	},

	/**
	 * resizes the wob to fit the rendered content
	 */
	fitWobToContent: function(firstRender) {
		this.$el.addClass('is-rendering')

		// Now that we've rendered the accessory, make sure the wob itself is sized appropriately (ie not too small).
		// Wait until next tick in case this is the original render and we aren't in the DOM yet.
		process.nextTick(() => {
			const label = this.model.properties.get('label')
			this.$('.title-text h3').text(label)
			this.$el.toggleClass('has-title', !!label)
			this.$el.removeClass('is-rendering')
			// If user toggles the title on/off, we need to resize the wob to
			// account for the change in height. However, we don't want to do
			// this on the initial render, because the wob will already be the
			// correct size. See #52861 && 54056 for more details.
			if (label && this.model.properties.hasChanged('label') && !firstRender) {
				let newHeight = this.$el.outerHeight() + this.$('.title-text').outerHeight()
				this.model.wob.resize(this.$el.outerWidth(), newHeight)
			}
			const autoFit = this.model.properties.get('autoFit')
			if (firstRender && autoFit) {
				// #24562 If this is the initial render, and the widget is set to
				// automatically adjust the wob so the contents fit, then do the
				// autofit. However, if the dashboard is saved, we want the new
				// dimensions to be used from now on, so set autoFit to false
				this.autoFit({height: true, width: true})
				this.model.properties.set('autoFit')
			} else {
				this.resize()
				// autoFit triggers a 2nd render
				this.readyForDisplayAndExport()
			}
		})
	},

	/**
	 * renders the content for the given accessory
	 *
	 * @abstract
	 */
	renderContent: function() {},

	/**
	 * turns the relevant setting of the chart for the given accessory
	 * to its "off position"
	 *
	 * @abstract
	 */
	removeFromChartWidget: function() {},

	/**
	 * gets the outermost/containing element for the accessory widget so that
	 * the widget can autofit to the content
	 *
	 * @abstract
	 */
	getWidgetContent: function() {},

	/**
	 * @abstract
	 */
	resize: function() {},

	/**
	 * @abstract
	 */
	defaultFit: function() {},

	removeEmptyState() {
		this.emptyState && this.emptyState.remove()
	},

	remove: function() {
		WidgetView.prototype.remove.apply(this, arguments)
		this.removeEmptyState()
	}
})

export default ChartAccessoryWidgetView
ChartAccessoryWidgetView.__filename__ = 'dashboards/widgets/chart/chart-accessory-widget-view.js'
